import { createFileRoute, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useServerFn } from '@tanstack/react-start'
import { motion } from 'motion/react'
import { Lock, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getLinkByCodeFn, recordClickFn } from '@/server/functions/links'
import { toast } from 'sonner'

export const Route = createFileRoute('/$code_/unlock')({
  component: UnlockPage,
})

function UnlockPage() {
  const { code } = useParams({ from: '/$code_/unlock' })
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const getLinkByCode = useServerFn(getLinkByCodeFn)
  const recordClick = useServerFn(recordClickFn)

  const handleUnlock = async () => {
    if (!password.trim()) {
      toast.error('Please enter a password')
      return
    }

    setIsLoading(true)
    try {
      const result = await getLinkByCode({
        data: {
          code,
          password,
        },
      })

      if (result.requiresPassword) {
        toast.error('Incorrect password')
        return
      }

      if (!result.link) {
        toast.error('Link not found')
        return
      }

      // Record the click
      await recordClick({
        data: {
          linkId: result.link.$id,
          shortCode: code,
        },
      })

      // Redirect to the destination
      window.location.href = result.link.originalUrl
    } catch (error) {
      toast.error('Failed to unlock link')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[#9B87F5]/10 rounded-full blur-[100px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <div className="bg-[#1A1F2C]/80 backdrop-blur-xl rounded-lg border border-[#9B87F5]/20 overflow-hidden">
          {/* Terminal header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-[#0a0a0a] border-b border-[#9B87F5]/10">
            <div className="w-3 h-3 rounded-full bg-red-500/70" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
            <div className="w-3 h-3 rounded-full bg-green-500/70" />
            <span className="text-[#8E9196] text-xs font-mono ml-2">
              unlock.exe
            </span>
          </div>

          <div className="p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-[#9B87F5]/10 rounded-lg">
                <Lock className="w-8 h-8 text-[#9B87F5]" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-white font-mono mb-2">
                PASSWORD_REQUIRED
              </h1>
              <p className="text-[#8E9196] font-mono text-sm">
                // This link is password protected
              </p>
            </div>

            {/* Password Input */}
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Enter password..."
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                className="bg-[#0a0a0a] border-[#9B87F5]/20 text-white font-mono focus:border-[#9B87F5] placeholder:text-[#8E9196]/50"
              />
              <Button
                onClick={handleUnlock}
                disabled={isLoading}
                className="w-full bg-[#9B87F5] hover:bg-[#8B5CF6] text-black font-mono font-bold h-12"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    UNLOCKING...
                  </>
                ) : (
                  '[ UNLOCK ]'
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom decoration */}
        <div className="text-center mt-4">
          <pre className="font-mono text-[#9B87F5]/20 text-[8px] inline-block">
            {`═══════════════════════════════
       SECURE_ACCESS
═══════════════════════════════`}
          </pre>
        </div>
      </motion.div>
    </div>
  )
}
